import { useFetchIncidentUser } from "@/lib/hooks/user";
import { useFetchVehicleZoneViolationIncident } from "@/lib/hooks/vehicle-zone-violation-incident";
import { useFetchVehicle } from "@/lib/hooks/vehicle";
import { useFetchDepartment } from "@/lib/hooks/department";
import { useFetchVehicleType } from "@/lib/hooks/vehicle-type";
import { useFetchShift } from "@/lib/hooks/shift";
import { Card } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { EventMetaCard } from "@/components/ui/event-meta-card";
import { OperatorDetails } from "@/components/ui/operator-details";
import { VehicleDetails } from "@/components/ui/vehicle-details";
import { useTranslation } from "react-i18next";

export type VehicleZoneViolationProps = {
    vehicleZoneViolationIncidentId: string;
};

export const VehicleZoneViolation = ({
    vehicleZoneViolationIncidentId,
}: VehicleZoneViolationProps): JSX.Element => {
    const { t } = useTranslation();
    const [vehicleZoneViolationIncident, isVehicleZoneViolationIncidentLoading] =
        useFetchVehicleZoneViolationIncident(vehicleZoneViolationIncidentId);

    const [aVehicle, isAVehicleLoading] = useFetchVehicle(vehicleZoneViolationIncident?.aVehicleId);
    const [aVehicleType, isAVehicleTypeLoading] = useFetchVehicleType(aVehicle?.vehicleTypeId);
    const [aVehicleDepartment, isAVehicleDepartmentLoading] = useFetchDepartment(
        aVehicle?.departmentId,
    );
    const [aUser, isAUserLoading] = useFetchIncidentUser(
        vehicleZoneViolationIncident?.aUserId,
        vehicleZoneViolationIncident?.aVehicleId,
        vehicleZoneViolationIncident?.startTime,
    );
    const [aUserDepartment, isAUserDepartmentLoading] = useFetchDepartment(aUser?.departmentId);
    const [aShift, isAShiftLoading] = useFetchShift(aUser?.shiftId);

    const [bVehicle, isBVehicleLoading] = useFetchVehicle(vehicleZoneViolationIncident?.bVehicleId);
    const [bVehicleType, isBVehicleTypeLoading] = useFetchVehicleType(bVehicle?.vehicleTypeId);
    const [bVehicleDepartment, isBVehicleDepartmentLoading] = useFetchDepartment(
        bVehicle?.departmentId,
    );
    const [bUser, isBUserLoading] = useFetchIncidentUser(
        vehicleZoneViolationIncident?.bUserId,
        vehicleZoneViolationIncident?.bVehicleId,
        vehicleZoneViolationIncident?.startTime,
    );
    const [bUserDepartment, isBUserDepartmentLoading] = useFetchDepartment(bUser?.departmentId);
    const [bShift, isBShiftLoading] = useFetchShift(bUser?.shiftId);

    const loading =
        isAShiftLoading ||
        isAUserDepartmentLoading ||
        isAUserLoading ||
        isAVehicleDepartmentLoading ||
        isAVehicleLoading ||
        isAVehicleTypeLoading ||
        isBShiftLoading ||
        isBUserDepartmentLoading ||
        isBUserLoading ||
        isBVehicleDepartmentLoading ||
        isBVehicleLoading ||
        isBVehicleTypeLoading ||
        isVehicleZoneViolationIncidentLoading;

    const vehicleACard = (
        <Card>
            <OperatorDetails
                title={t("component.operatorDetails.title.operator")}
                user={aUser}
                unknown={false}
                department={aUserDepartment}
                shift={aShift}
                badgeLabel={t("component.operatorDetails.badge.driver")}
                loading={loading}
            />
            <Separator className="mx-4" />
            <VehicleDetails
                department={aVehicleDepartment}
                vehicle={aVehicle}
                vehicleType={aVehicleType}
                loading={loading}
            />
        </Card>
    );
    const vehicleBCard = (
        <Card>
            <OperatorDetails
                title={t("component.operatorDetails.title.operator")}
                user={bUser}
                unknown={false}
                department={bUserDepartment}
                shift={bShift}
                badgeLabel={t("component.operatorDetails.badge.driver")}
                loading={loading}
            />
            <Separator className="mx-4" />
            <VehicleDetails
                department={bVehicleDepartment}
                vehicle={bVehicle}
                vehicleType={bVehicleType}
                loading={loading}
            />
        </Card>
    );
    // Order the cards based on the serial number to match the order in the incidents list.
    const cards =
        aVehicle && bVehicle && aVehicle.serialNumber <= bVehicle.serialNumber
            ? [vehicleACard, vehicleBCard]
            : [vehicleBCard, vehicleACard];

    return (
        <>
            <EventMetaCard
                vehicleZoneViolationIncident={vehicleZoneViolationIncident}
                loading={loading}
            />
            {cards}
        </>
    );
};
